// Vật phẩm xếp chồng (không phải trang bị): linh dược, hạt giống, đan, nguyên liệu (CRAFTING §1.3,
// §2.6, §6; ENEMIES §3). Linh dược mang niên phân: khóa chồng `herb.x` (thập niên) hoặc `herb.x@century`.
import type { Element } from '../elements';
import type { Phase } from '../screens';

export type GoodsKind = 'herb' | 'seed' | 'pill' | 'material';
export type HerbAge = 'decade' | 'century' | 'millennium' | 'myriad';
export const HERB_AGES: readonly HerbAge[] = ['decade', 'century', 'millennium', 'myriad'];

export interface GoodsDef {
  readonly id: string;
  readonly kind: GoodsKind;
  readonly element?: Element;
  /** Icon trong public/assets/items (không đuôi). */
  readonly icon?: string;
  /** Phẩm hiển thị (màu viền ô): chỉ số trong RARITIES. */
  readonly tier: number;
  readonly phase: Phase;
}

export const GOODS: readonly GoodsDef[] = [
  // Linh dược (CRAFTING §1.3, P0)
  { id: 'herb.dewGrass', kind: 'herb', element: 'wood', tier: 0, phase: 'P0' },
  { id: 'herb.yellowEssence', kind: 'herb', element: 'earth', tier: 1, phase: 'P0' },
  // Hạt giống
  { id: 'seed.dewGrass', kind: 'seed', element: 'wood', tier: 0, phase: 'P0' },
  { id: 'seed.yellowEssence', kind: 'seed', element: 'earth', tier: 1, phase: 'P0' },
  // Đan (P0: Trúc Cơ Đan luyện được; Tụ Khí, Hồi Xuân là vật phẩm tặng)
  { id: 'pill.foundation', kind: 'pill', element: 'earth', icon: 'hoi-nguyen-dan', tier: 3, phase: 'P0' },
  { id: 'pill.qiGathering', kind: 'pill', element: 'wood', icon: 'tu-linh-dan', tier: 1, phase: 'P0' },
  { id: 'pill.springRevival', kind: 'pill', element: 'wood', tier: 1, phase: 'P0' },
  // Nguyên liệu (CRAFTING §6)
  { id: 'mat.beastCore1', kind: 'material', element: 'metal', tier: 2, phase: 'P0' },
  { id: 'mat.springWater', kind: 'material', element: 'water', tier: 1, phase: 'P0' },
  { id: 'mat.pillDregs', kind: 'material', element: 'earth', tier: 0, phase: 'P0' },
  { id: 'mat.spiritWood', kind: 'material', element: 'wood', tier: 0, phase: 'P0' },
  { id: 'mat.darkIronSand', kind: 'material', element: 'earth', tier: 1, phase: 'P0' },
  // Nguyên liệu họ quái (ENEMIES §3): Lang, Hổ
  { id: 'mat.wolfFang', kind: 'material', element: 'metal', tier: 0, phase: 'P0' },
  { id: 'mat.wolfPelt', kind: 'material', element: 'metal', tier: 0, phase: 'P0' },
  { id: 'mat.bloodFang', kind: 'material', element: 'metal', tier: 2, phase: 'P0' },
  { id: 'mat.tigerBone', kind: 'material', element: 'fire', tier: 0, phase: 'P0' },
  { id: 'mat.tigerPelt', kind: 'material', element: 'fire', tier: 0, phase: 'P0' },
  { id: 'mat.tigerClaw', kind: 'material', element: 'fire', tier: 2, phase: 'P0' },
];

export function goodsDef(id: string): GoodsDef {
  const d = GOODS.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown goods: ${id}`);
  return d;
}

export function isGoodsId(v: unknown): v is string {
  return typeof v === 'string' && GOODS.some((g) => g.id === v);
}

export type StackGrade = 'low' | 'mid' | 'high' | 'peak';
export const STACK_GRADES: readonly StackGrade[] = ['low', 'mid', 'high', 'peak'];

/**
 * Khóa chồng: `herb.x` (thập niên) · `herb.x@century` (niên phân) · `pill.x#mid` (phẩm đan).
 * Đan không ghi phẩm coi là trung phẩm.
 */
export function parseStackKey(key: string): { id: string; age: HerbAge; grade: StackGrade } {
  const [head, grade] = key.split('#') as [string, string | undefined];
  const [id, age] = head.split('@') as [string, string | undefined];
  return {
    id,
    age: (HERB_AGES as readonly string[]).includes(age ?? '') ? (age as HerbAge) : 'decade',
    grade: (STACK_GRADES as readonly string[]).includes(grade ?? '') ? (grade as StackGrade) : 'mid',
  };
}

export function stackKey(id: string, o: { age?: HerbAge; grade?: StackGrade } = {}): string {
  if (goodsDef(id).kind === 'pill') return `${id}#${o.grade ?? 'mid'}`;
  return !o.age || o.age === 'decade' ? id : `${id}@${o.age}`;
}

export function isStackKey(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const [head, grade] = v.split('#');
  const [id, age] = (head ?? '').split('@');
  if (!isGoodsId(id)) return false;
  const kind = goodsDef(id).kind;
  if (kind === 'pill') return age === undefined && (STACK_GRADES as readonly string[]).includes(grade ?? '');
  return (
    grade === undefined &&
    (age === undefined || (kind === 'herb' && (HERB_AGES as readonly string[]).includes(age)))
  );
}

/** Bậc niên phân từ số năm (CRAFTING §1.2). */
export function herbAgeOf(years: number): HerbAge {
  return years >= 10_000 ? 'myriad' : years >= 1000 ? 'millennium' : years >= 100 ? 'century' : 'decade';
}
